/** Rich solid-color picker — Tier 3 Group D Slice 1a.
 *
 *  The picker the gradient-mode popover Solid tab and the solid-only
 *  inline-mode popover both render. Self-contained: parent passes
 *  `value` (hex) and `onChange`; the component owns mode tabs, the
 *  SV square, the hue slider, EyeDropper integration, and the
 *  persisted recent-colors strip.
 *
 *  Why the picker holds its own `hueDraft` instead of deriving hue
 *  from `value` each render:
 *    At black / white / pure gray the hue is mathematically
 *    undefined and `rgbToHsb` reports 0. If the slider re-derived
 *    every render the indicator would snap back to red whenever
 *    the user dragged into black — a frustrating regression of the
 *    classic Photoshop fix. Tracking the user's last-chosen hue
 *    separately keeps the slider position stable across achromatic
 *    values. The same approach holds for saturation when V=0 (any
 *    black is achromatic regardless of S). */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Pipette, Plus, Settings2, X } from 'lucide-react';
import {
  parseHex,
  rgbToHex,
  rgbToHsb,
  hsbToRgb,
  rgbToHsl,
  hslToRgb,
  rgbToLab,
  labToRgb,
  type Rgb,
} from '../lib/color-conversion';
import { isEyeDropperSupported, pickColorWithEyeDropper } from '../lib/eyedropper';
import { useRecentColors } from '../lib/use-recent-colors';
import { usePalettes } from '../lib/use-palettes';
import type { Palette } from '../lib/builtin-palettes';
import { PalettesDialog } from './PalettesDialog';

type Mode = 'hsb' | 'rgb' | 'hsl' | 'lab' | 'hex';

const MODES: { id: Mode; label: string }[] = [
  { id: 'hsb', label: 'HSB' },
  { id: 'rgb', label: 'RGB' },
  { id: 'hsl', label: 'HSL' },
  { id: 'lab', label: 'Lab' },
  { id: 'hex', label: 'Hex' },
];

interface Props {
  /** Current color as `#rrggbb`. The `transparent` keyword is
   *  treated as black inside the picker; the parent component
   *  is responsible for surfacing alpha / transparency separately. */
  value: string;
  /** Called with `#rrggbb` whenever the user commits a change
   *  (typing in a numeric field, dragging the SV square, etc.). */
  onChange: (hex: string) => void;
}

export function ColorPickerCustom({ value, onChange }: Props) {
  const incomingHex = value === 'transparent' ? '#000000' : value;
  const rgb = useMemo<Rgb>(() => parseHex(incomingHex) ?? { r: 0, g: 0, b: 0 }, [incomingHex]);

  // Hue / saturation stick around through achromatic values — see
  // the file header comment on why we don't recompute from `value`.
  const [hueDraft, setHueDraft] = useState(() => rgbToHsb(rgb).h);
  const lastIncomingRef = useRef(incomingHex);

  // When the value changes externally (e.g. parent re-syncs from a
  // different layer's color), only re-derive hue if the new color
  // actually has a defined hue. Black / gray keep the user's last
  // hue position.
  useEffect(() => {
    if (lastIncomingRef.current === incomingHex) return;
    lastIncomingRef.current = incomingHex;
    const next = rgbToHsb(rgb);
    if (next.s > 0 && next.b > 0) {
      setHueDraft(next.h);
    }
  }, [incomingHex, rgb]);

  const [mode, setMode] = useState<Mode>('hsb');
  const { recentColors, pushRecent } = useRecentColors();

  const commit = useCallback(
    (next: Rgb) => {
      const hex = rgbToHex(next);
      onChange(hex);
      pushRecent(hex);
    },
    [onChange, pushRecent],
  );

  // Track drag without committing to recents until pointer-up —
  // a single click+drag across the SV square shouldn't dump 200
  // entries onto the recents strip. We commit-without-recent on
  // every pointermove and commit-with-recent on pointerup.
  const commitLive = useCallback(
    (next: Rgb) => {
      onChange(rgbToHex(next));
    },
    [onChange],
  );

  const eyedropperSupported = useMemo(() => isEyeDropperSupported(), []);

  const onEyedropper = useCallback(async () => {
    const hex = await pickColorWithEyeDropper();
    if (hex) {
      const picked = parseHex(hex);
      if (picked) commit(picked);
    }
  }, [commit]);

  return (
    <div className="space-y-2">
      <SaturationValueSquare
        hue={hueDraft}
        rgb={rgb}
        onChange={(next, settled) => (settled ? commit(next) : commitLive(next))}
      />
      <HueSlider
        hue={hueDraft}
        onChange={(h, settled) => {
          setHueDraft(h);
          const hsb = rgbToHsb(rgb);
          const next = hsbToRgb({ h, s: Math.max(hsb.s, 1), b: Math.max(hsb.b, 1) });
          if (settled) commit(next);
          else commitLive(next);
        }}
      />

      <div className="flex items-center gap-1">
        <div className="flex flex-1 gap-0.5 rounded-md bg-muted p-0.5">
          {MODES.map((m) => (
            <button
              key={m.id}
              type="button"
              aria-pressed={mode === m.id}
              className={`flex-1 px-1.5 py-0.5 text-[10px] font-medium rounded ${
                mode === m.id
                  ? 'bg-background text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
              onClick={() => setMode(m.id)}
            >
              {m.label}
            </button>
          ))}
        </div>
        {eyedropperSupported && (
          <button
            type="button"
            aria-label="Pick color from screen"
            title="Pick color from screen"
            onClick={onEyedropper}
            className="p-1.5 rounded-md border border-border text-muted-foreground hover:text-foreground hover:bg-accent"
          >
            <Pipette className="h-3.5 w-3.5" />
          </button>
        )}
      </div>

      <ModePanel mode={mode} rgb={rgb} hue={hueDraft} setHue={setHueDraft} commit={commit} />

      {recentColors.length > 0 && <RecentColorsStrip colors={recentColors} onPick={commit} />}

      <SwatchesSection currentHex={rgbToHex(rgb)} onPick={commit} />
    </div>
  );
}

// ─── Swatches section (built-in + user palettes) ───────────────

function SwatchesSection({
  currentHex,
  onPick,
}: {
  currentHex: string;
  onPick: (rgb: Rgb) => void;
}) {
  const { allPalettes, activePalette, selectPalette, addColor } = usePalettes();
  const [manageOpen, setManageOpen] = useState(false);
  const isUserPalette = activePalette.kind === 'user';
  const canAddCurrent =
    isUserPalette && !activePalette.groups.some((g) => g.colors.includes(currentHex));

  return (
    <>
      <div className="space-y-1">
        <div className="flex items-center gap-1">
          <select
            aria-label="Active palette"
            value={activePalette.id}
            onChange={(e) => selectPalette(e.target.value)}
            className="flex-1 min-w-0 text-[11px] bg-background border border-border rounded px-1.5 py-0.5"
          >
            <optgroup label="Built-in">
              {allPalettes
                .filter((p) => p.kind === 'builtin')
                .map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
            </optgroup>
            {allPalettes.some((p) => p.kind === 'user') && (
              <optgroup label="My palettes">
                {allPalettes
                  .filter((p) => p.kind === 'user')
                  .map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                    </option>
                  ))}
              </optgroup>
            )}
          </select>
          <button
            type="button"
            aria-label="Add current color to palette"
            title={
              isUserPalette
                ? canAddCurrent
                  ? 'Add current color to this palette'
                  : 'Color already in palette'
                : 'Create a user palette to add colors'
            }
            disabled={!canAddCurrent}
            onClick={() => addColor(activePalette.id, currentHex)}
            className="p-1 rounded-md border border-border text-muted-foreground hover:text-foreground hover:bg-accent disabled:opacity-30 disabled:hover:bg-transparent"
          >
            <Plus className="h-3 w-3" />
          </button>
          <button
            type="button"
            aria-label="Manage palettes"
            title="Manage palettes"
            onClick={() => setManageOpen(true)}
            className="p-1 rounded-md border border-border text-muted-foreground hover:text-foreground hover:bg-accent"
          >
            <Settings2 className="h-3 w-3" />
          </button>
        </div>
        <PaletteSwatches palette={activePalette} onPick={onPick} />
      </div>
      {manageOpen && <PalettesDialog onClose={() => setManageOpen(false)} />}
    </>
  );
}

function PaletteSwatches({ palette, onPick }: { palette: Palette; onPick: (rgb: Rgb) => void }) {
  const hasGroups = palette.groups.length > 1 || palette.groups[0]?.label;
  if (palette.groups.every((g) => g.colors.length === 0)) {
    return (
      <p className="text-[10px] text-muted-foreground italic py-1">
        No colors yet — pick a color and tap + to add.
      </p>
    );
  }
  return (
    <div className="max-h-32 overflow-y-auto pr-1 space-y-1">
      {palette.groups.map((g, i) => (
        <div key={i}>
          {hasGroups && g.label ? (
            <div className="text-[9px] text-muted-foreground uppercase tracking-wide pb-0.5">
              {g.label}
            </div>
          ) : null}
          <div className="grid grid-cols-10 gap-0.5">
            {g.colors.map((c) => (
              <button
                key={c}
                type="button"
                aria-label={`Use ${c}`}
                title={c}
                className="aspect-square rounded-sm border border-border cursor-pointer hover:scale-110 transition-transform"
                style={{ background: c }}
                onClick={() => {
                  const parsed = parseHex(c);
                  if (parsed) onPick(parsed);
                }}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── SV (saturation/value) square ───────────────────────────────

function SaturationValueSquare({
  hue,
  rgb,
  onChange,
}: {
  hue: number;
  rgb: Rgb;
  onChange: (next: Rgb, settled: boolean) => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const hsb = rgbToHsb(rgb);
  // The visible indicator should track the *displayed* HSB even
  // when the color is achromatic — at black (V=0), S is
  // mathematically zero, so the indicator goes to the bottom-left
  // (S=0, V=0). That matches Photoshop / Figma behavior.
  const sx = hsb.s;
  const sy = 100 - hsb.b;

  const updateFromEvent = useCallback(
    (e: PointerEvent | React.PointerEvent, settled: boolean) => {
      const el = ref.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const s = clamp01((e.clientX - rect.left) / rect.width) * 100;
      const v = (1 - clamp01((e.clientY - rect.top) / rect.height)) * 100;
      onChange(hsbToRgb({ h: hue, s, b: v }), settled);
    },
    [hue, onChange],
  );

  const onPointerDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      e.preventDefault();
      const el = ref.current;
      if (!el) return;
      el.setPointerCapture(e.pointerId);
      updateFromEvent(e, false);
      const onMove = (ev: PointerEvent) => updateFromEvent(ev, false);
      const onUp = (ev: PointerEvent) => {
        updateFromEvent(ev, true);
        el.releasePointerCapture(e.pointerId);
        el.removeEventListener('pointermove', onMove);
        el.removeEventListener('pointerup', onUp);
        el.removeEventListener('pointercancel', onUp);
      };
      el.addEventListener('pointermove', onMove);
      el.addEventListener('pointerup', onUp);
      el.addEventListener('pointercancel', onUp);
    },
    [updateFromEvent],
  );

  // Keyboard navigation — Arrow keys nudge by 1% S/V, Shift+Arrow
  // nudges by 10%. Home/End jump to extremes on the focused axis.
  // ARIA spec requires `role="slider"` to be keyboard operable;
  // without these handlers screen-reader users would announce the
  // role but have no way to actually move the indicator.
  const onKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      const step = e.shiftKey ? 10 : 1;
      const dx = e.key === 'ArrowLeft' ? -step : e.key === 'ArrowRight' ? step : 0;
      const dy = e.key === 'ArrowUp' ? step : e.key === 'ArrowDown' ? -step : 0;
      if (dx === 0 && dy === 0 && e.key !== 'Home' && e.key !== 'End') return;
      e.preventDefault();
      const targetS =
        e.key === 'Home' ? 0 : e.key === 'End' ? 100 : Math.max(0, Math.min(100, hsb.s + dx));
      const targetV = Math.max(0, Math.min(100, hsb.b + dy));
      onChange(hsbToRgb({ h: hue, s: targetS, b: targetV }), true);
    },
    [hsb.s, hsb.b, hue, onChange],
  );

  return (
    <div
      ref={ref}
      role="slider"
      aria-label="Saturation and brightness"
      aria-valuetext={`Saturation ${Math.round(hsb.s)}%, brightness ${Math.round(hsb.b)}%`}
      tabIndex={0}
      className="relative w-full h-44 rounded-md border border-border cursor-crosshair touch-none select-none overflow-hidden focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
      style={{
        background: `
          linear-gradient(to top, #000, transparent),
          linear-gradient(to right, #fff, hsl(${hue}, 100%, 50%))
        `,
      }}
      onPointerDown={onPointerDown}
      onKeyDown={onKeyDown}
    >
      <span
        className="absolute -translate-x-1/2 -translate-y-1/2 w-3 h-3 rounded-full border-2 border-white shadow-[0_0_0_1px_rgba(0,0,0,0.5)] pointer-events-none"
        style={{ left: `${sx}%`, top: `${sy}%` }}
        aria-hidden="true"
      />
    </div>
  );
}

// ─── Hue slider ─────────────────────────────────────────────────

function HueSlider({
  hue,
  onChange,
}: {
  hue: number;
  onChange: (h: number, settled: boolean) => void;
}) {
  const ref = useRef<HTMLDivElement>(null);

  const updateFromEvent = useCallback(
    (e: PointerEvent | React.PointerEvent, settled: boolean) => {
      const el = ref.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const h = clamp01((e.clientX - rect.left) / rect.width) * 360;
      onChange(h, settled);
    },
    [onChange],
  );

  const onPointerDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      e.preventDefault();
      const el = ref.current;
      if (!el) return;
      el.setPointerCapture(e.pointerId);
      updateFromEvent(e, false);
      const onMove = (ev: PointerEvent) => updateFromEvent(ev, false);
      const onUp = (ev: PointerEvent) => {
        updateFromEvent(ev, true);
        el.releasePointerCapture(e.pointerId);
        el.removeEventListener('pointermove', onMove);
        el.removeEventListener('pointerup', onUp);
        el.removeEventListener('pointercancel', onUp);
      };
      el.addEventListener('pointermove', onMove);
      el.addEventListener('pointerup', onUp);
      el.addEventListener('pointercancel', onUp);
    },
    [updateFromEvent],
  );

  // Keyboard navigation — Arrow keys nudge the hue by 1°, Shift+Arrow
  // by 10°. Home/End jump to 0°/360°. Mirrors the SV square's
  // keyboard handler so ARIA `role="slider"` actually behaves.
  const onKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      const step = e.shiftKey ? 10 : 1;
      const delta = e.key === 'ArrowLeft' ? -step : e.key === 'ArrowRight' ? step : 0;
      if (delta === 0 && e.key !== 'Home' && e.key !== 'End') return;
      e.preventDefault();
      const target =
        e.key === 'Home' ? 0 : e.key === 'End' ? 360 : Math.max(0, Math.min(360, hue + delta));
      onChange(target, true);
    },
    [hue, onChange],
  );

  return (
    <div
      ref={ref}
      role="slider"
      aria-label="Hue"
      aria-valuemin={0}
      aria-valuemax={360}
      aria-valuenow={Math.round(hue)}
      tabIndex={0}
      className="relative w-full h-3 rounded-md cursor-pointer touch-none select-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
      style={{
        background:
          'linear-gradient(to right, hsl(0,100%,50%), hsl(60,100%,50%), hsl(120,100%,50%), hsl(180,100%,50%), hsl(240,100%,50%), hsl(300,100%,50%), hsl(360,100%,50%))',
      }}
      onPointerDown={onPointerDown}
      onKeyDown={onKeyDown}
    >
      <span
        className="absolute top-1/2 -translate-x-1/2 -translate-y-1/2 w-3 h-3 rounded-full border-2 border-white shadow-[0_0_0_1px_rgba(0,0,0,0.5)] bg-transparent pointer-events-none"
        style={{ left: `${(hue / 360) * 100}%` }}
        aria-hidden="true"
      />
    </div>
  );
}

// ─── Per-mode numeric panel ─────────────────────────────────────

function ModePanel({
  mode,
  rgb,
  hue,
  setHue,
  commit,
}: {
  mode: Mode;
  rgb: Rgb;
  hue: number;
  setHue: (h: number) => void;
  commit: (rgb: Rgb) => void;
}) {
  switch (mode) {
    case 'hsb':
      return <HsbPanel rgb={rgb} hue={hue} setHue={setHue} commit={commit} />;
    case 'rgb':
      return <RgbPanel rgb={rgb} commit={commit} />;
    case 'hsl':
      return <HslPanel rgb={rgb} hue={hue} setHue={setHue} commit={commit} />;
    case 'lab':
      return <LabPanel rgb={rgb} commit={commit} />;
    case 'hex':
      return <HexPanel rgb={rgb} commit={commit} />;
  }
}

function HsbPanel({
  rgb,
  hue,
  setHue,
  commit,
}: {
  rgb: Rgb;
  hue: number;
  setHue: (h: number) => void;
  commit: (rgb: Rgb) => void;
}) {
  const hsb = rgbToHsb(rgb);
  return (
    <div className="grid grid-cols-3 gap-1.5">
      <NumberField
        label="H"
        suffix="°"
        value={Math.round(hue)}
        min={0}
        max={360}
        onChange={(h) => {
          setHue(h);
          commit(hsbToRgb({ h, s: hsb.s, b: hsb.b }));
        }}
      />
      <NumberField
        label="S"
        suffix="%"
        value={Math.round(hsb.s)}
        min={0}
        max={100}
        onChange={(s) => commit(hsbToRgb({ h: hue, s, b: hsb.b }))}
      />
      <NumberField
        label="B"
        suffix="%"
        value={Math.round(hsb.b)}
        min={0}
        max={100}
        onChange={(b) => commit(hsbToRgb({ h: hue, s: hsb.s, b }))}
      />
    </div>
  );
}

function RgbPanel({ rgb, commit }: { rgb: Rgb; commit: (rgb: Rgb) => void }) {
  return (
    <div className="grid grid-cols-3 gap-1.5">
      <NumberField
        label="R"
        value={rgb.r}
        min={0}
        max={255}
        onChange={(r) => commit({ ...rgb, r })}
      />
      <NumberField
        label="G"
        value={rgb.g}
        min={0}
        max={255}
        onChange={(g) => commit({ ...rgb, g })}
      />
      <NumberField
        label="B"
        value={rgb.b}
        min={0}
        max={255}
        onChange={(b) => commit({ ...rgb, b })}
      />
    </div>
  );
}

function HslPanel({
  rgb,
  hue,
  setHue,
  commit,
}: {
  rgb: Rgb;
  hue: number;
  setHue: (h: number) => void;
  commit: (rgb: Rgb) => void;
}) {
  const hsl = rgbToHsl(rgb);
  return (
    <div className="grid grid-cols-3 gap-1.5">
      <NumberField
        label="H"
        suffix="°"
        value={Math.round(hue)}
        min={0}
        max={360}
        onChange={(h) => {
          setHue(h);
          commit(hslToRgb({ h, s: hsl.s, l: hsl.l }));
        }}
      />
      <NumberField
        label="S"
        suffix="%"
        value={Math.round(hsl.s)}
        min={0}
        max={100}
        onChange={(s) => commit(hslToRgb({ h: hue, s, l: hsl.l }))}
      />
      <NumberField
        label="L"
        suffix="%"
        value={Math.round(hsl.l)}
        min={0}
        max={100}
        onChange={(l) => commit(hslToRgb({ h: hue, s: hsl.s, l }))}
      />
    </div>
  );
}

function LabPanel({ rgb, commit }: { rgb: Rgb; commit: (rgb: Rgb) => void }) {
  const lab = rgbToLab(rgb);
  return (
    <div className="grid grid-cols-3 gap-1.5">
      <NumberField
        label="L"
        value={Math.round(lab.l)}
        min={0}
        max={100}
        onChange={(l) => commit(labToRgb({ l, a: lab.a, b: lab.b }))}
      />
      <NumberField
        label="a"
        value={Math.round(lab.a)}
        min={-128}
        max={127}
        onChange={(a) => commit(labToRgb({ l: lab.l, a, b: lab.b }))}
      />
      <NumberField
        label="b"
        value={Math.round(lab.b)}
        min={-128}
        max={127}
        onChange={(b) => commit(labToRgb({ l: lab.l, a: lab.a, b }))}
      />
    </div>
  );
}

/** Hex text field. Accepts paste of `#rrggbb`, `rrggbb`, `#rgb`,
 *  or `rgb` (with or without leading hash). The committed value
 *  is the canonical 6-char lowercase form. */
function HexPanel({ rgb, commit }: { rgb: Rgb; commit: (rgb: Rgb) => void }) {
  const canonical = rgbToHex(rgb);
  const [draft, setDraft] = useState(canonical);
  useEffect(() => setDraft(canonical), [canonical]);

  const tryCommit = useCallback(
    (raw: string) => {
      const parsed = parseHex(raw);
      if (parsed) commit(parsed);
    },
    [commit],
  );

  return (
    <div className="flex items-center gap-2">
      <span className="text-[10px] text-muted-foreground uppercase tabular-nums">Hex</span>
      <input
        type="text"
        value={draft}
        onChange={(e) => {
          const v = e.target.value;
          setDraft(v);
          tryCommit(v);
        }}
        onBlur={() => {
          // On blur, snap back to the committed canonical form so a
          // half-typed string (e.g. "#ff") doesn't linger after the
          // user clicks away.
          setDraft(canonical);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
        }}
        className="flex-1 min-w-0 text-xs font-mono bg-background border border-border rounded px-2 py-1"
        placeholder="#000000"
        spellCheck={false}
        aria-label="Hex color"
        maxLength={7}
      />
    </div>
  );
}

// ─── Numeric field with up/down arrow + manual entry ─────────

function NumberField({
  label,
  suffix,
  value,
  min,
  max,
  onChange,
}: {
  label: string;
  suffix?: string;
  value: number;
  min: number;
  max: number;
  onChange: (n: number) => void;
}) {
  const [draft, setDraft] = useState(String(value));
  useEffect(() => setDraft(String(value)), [value]);

  const commit = useCallback(
    (raw: string) => {
      const n = parseFloat(raw);
      if (Number.isFinite(n)) {
        onChange(Math.max(min, Math.min(max, n)));
      }
    },
    [min, max, onChange],
  );

  return (
    <label className="flex flex-col gap-0.5">
      <span className="text-[10px] text-muted-foreground uppercase">
        {label}
        {suffix ? ` (${suffix})` : ''}
      </span>
      <input
        type="number"
        value={draft}
        min={min}
        max={max}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={(e) => {
          commit(e.target.value);
          setDraft(String(value));
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            commit((e.target as HTMLInputElement).value);
            (e.target as HTMLInputElement).blur();
          }
        }}
        className="text-xs font-mono bg-background border border-border rounded px-1.5 py-1 tabular-nums w-full"
      />
    </label>
  );
}

// ─── Recent-colors strip ────────────────────────────────────────

function RecentColorsStrip({ colors, onPick }: { colors: string[]; onPick: (rgb: Rgb) => void }) {
  const { clearRecents } = useRecentColors();
  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between">
        <span className="text-[10px] text-muted-foreground uppercase">Recent</span>
        <button
          type="button"
          aria-label="Clear recents"
          title="Clear recents"
          onClick={clearRecents}
          className="p-0.5 text-muted-foreground hover:text-foreground"
        >
          <X className="h-3 w-3" />
        </button>
      </div>
      <div className="grid grid-cols-8 gap-1">
        {colors.slice(0, 16).map((c, i) => (
          <button
            key={`${c}-${i}`}
            type="button"
            aria-label={`Use ${c}`}
            title={c}
            className="w-5 h-5 rounded border border-border cursor-pointer"
            style={{ background: c }}
            onClick={() => {
              const parsed = parseHex(c);
              if (parsed) onPick(parsed);
            }}
          />
        ))}
      </div>
    </div>
  );
}

function clamp01(n: number): number {
  if (!Number.isFinite(n)) return 0;
  return Math.max(0, Math.min(1, n));
}
